// EditExitModal.jsx
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import api from '../../lib/api';
import { useTranslation } from 'react-i18next';

const EditExitModal = ({ isOpen, onClose, record, locationKey, onUpdated }) => {
  const { t } = useTranslation();
  // Safe date formatting (always returns yyyy-mm-dd)
  const safeDate = (value) => {
    if (!value) return new Date().toISOString().split('T')[0];
    try {
      // Usamos split para obtener solo la parte de la fecha y evitar desfases en la carga inicial
      return new Date(value).toISOString().split('T')[0];
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  };

  // Local editable state
  const [quantityExited, setQuantityExited] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [notes, setNotes] = useState('');
  const [exitedAt, setExitedAt] = useState(safeDate());

  // Load initial record values
  useEffect(() => {
    if (record) {
      setQuantityExited(record.quantityExited || 0);
      setTotalWeight(record.totalWeight || 0);
      setNotes(record.notes || '');
      setExitedAt(safeDate(record.exitedAt));
    }
  }, [record]);

  if (!isOpen || !record) return null;

  // SAFE MODE: only update notes + date
  const handleUpdate = async () => {
    try {
      // Agregamos T12:00:00 para forzar que la fecha sea mediodía local.
      // Esto evita que el toISOString() reste un día debido a zonas horarias negativas (GMT-6, etc.)
      const dateWithTime = exitedAt + 'T12:00:00';
      
      const payload = {
        notes,
        // Convert to ISO string matching the working Entry logic
        exitedAt: new Date(dateWithTime).toISOString(),
      };

      // Updated path to /inventory/exits
      await api.put(
        `/inventory/exits/${record.id || record._id}?location=${locationKey}`,
        payload
      );

      onUpdated();
      onClose();
    } catch (err) {
      console.error(err);
      alert(t('errorUpdatingExit'));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-lg p-6 relative">

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-700 dark:text-gray-300 hover:text-black"
        >
          <X size={22} />
        </button>

        <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
          {t('editExit')}
        </h2>

        {/* Read-only info */}
        <div className="space-y-3 mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <strong>{t('product')}:</strong> {record.productName}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <strong>{t('brand')}:</strong> {record.brandName}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <strong>{t('supplier')}:</strong> {record.supplierName}
          </p>
        </div>

        {/* Editable fields */}
        <div className="space-y-3">

          {/* Quantity (now locked) */}
          <div>
            <label className="text-sm font-medium">{t('quantity')}</label>
            <input
              disabled
              type="number"
              value={quantityExited}
              className="w-full mt-1 px-3 py-2 rounded-lg border opacity-50 cursor-not-allowed dark:bg-gray-800 dark:text-white"
            />
            <p className="text-xs text-gray-500">{t('disabledQuantityEdit')}</p>
          </div>

          {/* Total Weight (now locked) */}
          <div>
            <label className="text-sm font-medium">{t('totalWeightLabel')}</label>
            <input
              disabled
              type="number"
              value={totalWeight}
              className="w-full mt-1 px-3 py-2 rounded-lg border opacity-50 cursor-not-allowed dark:bg-gray-800 dark:text-white"
            />
            <p className="text-xs text-gray-500">{t('disabledWeightEdit')}</p>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium">{t('notes')}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg border dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Date */}
          <div>
            <label className="text-sm font-medium">{t('date')}</label>
            <input
              type="date"
              value={exitedAt}
              onChange={(e) => setExitedAt(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg border dark:bg-gray-800 dark:text-white"
            />
          </div>

        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-300 dark:bg-gray-700 text-black dark:text-white"
          >
            {t('cancel')}
          </button>

          <button
            onClick={handleUpdate}
            className="px-4 py-2 rounded-lg bg-black text-white dark:bg-white dark:text-black hover:scale-105 transition"
          >
            Update
          </button>
        </div>

      </div>
    </div>
  );
};

export default EditExitModal;
